// Libs
import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { negate } from 'lodash/fp';

// Actions
import { moveMultipleElements } from '../../../../../../element/actions/elementMoveActions';
import { forceElementsMeasurements } from '../../../../../../components/measurementsStore/elementMeasurements/elementMeasurementsActions';

// Selectors
import { getCurrentBoard } from '../../../../../../reducers/currentBoardId/currentBoardIdSelector';
import { getMeasurementsMapThunk } from '../../../../../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import { useMeasurementsDispatch } from '../../../../../../components/measurementsStore/MeasurementsProvider';

// Components
import ArrangementPopup from '../ArrangementPopup';
import Button from '../../../../../../components/buttons/Button';
import Icon from '../../../../../../components/icons/Icon';
import TooltipSource from '../../../../../../components/tooltips/TooltipSource';

// Utils
import { toArray } from '../../../../../../../common/utils/immutableHelper';
import { getElementId, isElementLocked } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { getHorizontallyDistributedMoves, getVerticallyDistributedMoves } from './distributionUtils';

// Constants
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';
import { TooltipPositions } from '../../../../../../components/tooltips/tooltipConstants';
import { ELEMENT_MOVE_OPERATIONS } from '../../../../../../../common/elements/elementConstants';
import { ActionOrigin } from '../../../../../../../common/actions/actionTypes';

// Types
import { UnknownAction } from 'redux';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { ImElementMeasurementMap } from '../../../../../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { GetLayoutActionFn } from '../arrangementUtils';

// Styles
import './DistributePopup.scss';
import { List } from 'immutable';

type DistributePopupProps = {
    gridSize: number;
    selectedElements: List<ImMNElement>;
    closePopup: () => void;
};

const DistributePopup: FC<DistributePopupProps> = ({ selectedElements, closePopup, gridSize }) => {
    const currentBoard = useSelector(getCurrentBoard);
    const unlockedSelectedElements = selectedElements.filter(negate(isElementLocked));

    const dispatch = useDispatch();
    const dispatchMoveMultipleElements = useCallback(
        (moves) =>
            dispatch(
                moveMultipleElements({
                    moves,
                    moveOperation: ELEMENT_MOVE_OPERATIONS.ALIGN,
                    initialMeasurements: undefined,
                    actionOrigin: ActionOrigin.arrangementTool,
                }),
            ),
        [dispatch],
    );
    const dispatchForceElementsMeasurements = useCallback(
        (elementIds) => dispatch(forceElementsMeasurements(elementIds)),
        [dispatch],
    );

    const measurementsDispatch = useMeasurementsDispatch();
    // Typescript has difficulties with immutable & thunks independently, so we need to cast it here
    const dispatchGetMeasurementsMap = () =>
        measurementsDispatch(
            getMeasurementsMapThunk() as unknown as UnknownAction,
        ) as unknown as ImElementMeasurementMap;

    const useLayoutFunction = (layoutFunction: GetLayoutActionFn) =>
        useCallback(async () => {
            const elementIds = toArray(unlockedSelectedElements.map(getElementId));
            await dispatchForceElementsMeasurements(elementIds);

            const measurements = dispatchGetMeasurementsMap();
            const moves = layoutFunction({
                measurements,
                elements: unlockedSelectedElements,
                currentBoard,
                gridSize,
            });
            dispatchMoveMultipleElements(moves);
            closePopup();
        }, [
            dispatchForceElementsMeasurements,
            dispatchGetMeasurementsMap,
            unlockedSelectedElements,
            currentBoard,
            gridSize,
            layoutFunction,
            dispatchMoveMultipleElements,
            closePopup,
        ]);

    const distributeHorizontally = useLayoutFunction(getHorizontallyDistributedMoves);
    const distributeVertically = useLayoutFunction(getVerticallyDistributedMoves);

    return (
        <ArrangementPopup
            popupId={PopupIds.DISTRIBUTE}
            className="DistributePopup"
            buttonSelector=".DistributeTool .icon"
        >
            <Button onClickFn={distributeHorizontally} disabled={unlockedSelectedElements.size <= 2}>
                <TooltipSource
                    enabled
                    tooltipText="Distribute horizontally"
                    position={TooltipPositions.RIGHT}
                    delay={700}
                    pollPosition
                    triggerOnMouseEnter
                >
                    <Icon name="distribute-horizontally" />
                </TooltipSource>
            </Button>
            <Button onClickFn={distributeVertically} disabled={unlockedSelectedElements.size <= 2}>
                <TooltipSource
                    enabled
                    tooltipText="Distribute vertically"
                    position={TooltipPositions.RIGHT}
                    delay={700}
                    pollPosition
                    triggerOnMouseEnter
                >
                    <Icon name="distribute-vertically" />
                </TooltipSource>
            </Button>
        </ArrangementPopup>
    );
};

export default DistributePopup;
